import React from 'react';
import { useAuth } from '../../context/AuthContext';
import PortalLayout from './PortalLayout';
import SEO from '../../components/SEO';
import { ROLES } from '../../constants/tiers';
import { LOCAL_DEAL_CATEGORIES } from '../../constants/localDeals';
import SubmitLocalDealModal from '../../components/SubmitLocalDealModal';
import { Tag, MapPin, Calendar, ExternalLink, Lock, Plus } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Mundalo - Local Deals (Groupon-style page).
 *
 * 15 curated, real Mexico-expat-relevant deals across CDMX, Playa, Mérida,
 * Puerto Vallarta, San Miguel. Categories: coworking, language schools,
 * restaurants, health services, activities.
 *
 * Honest copy: "Updated weekly" (not "auto-refreshed" - we don't scrape).
 * Admin can add/edit/remove via the admin dashboard.
 *
 * Access: Community + Connected only.
 *
 * Backend: deals are fetched from /api/local-deals. If admin hasn't seeded
 * yet, this curated list is the fallback.
 */

const FALLBACK_DEALS = [
  // === Coworking ===
  { id: 'wework-cdmx-day', category: 'Coworking', name: 'WeWork CDMX - Day Pass', city: 'Mexico City', description: 'All-day access to any WeWork in Reforma, Polanco, Roma, or Condesa. Includes coffee, printing, and meeting room time.', discount: '20% off first 5 day passes', valid_until: '2026-12-31', url: 'https://www.wework.com/buildings/mexico-city' },
  { id: 'selina-playa-coworking', category: 'Coworking', name: 'Selina Playa - Weekly Coworking', city: 'Playa del Carmen', description: 'Beachfront coworking week pass. Wi-Fi, AC, meeting pods, hot desks, oceanview lounge. Includes 1 free yoga class.', discount: '15% off weekly pass', valid_until: '2026-09-30', url: 'https://www.selina.com/mexico/playa-del-carmen/' },
  { id: 'cowork-merida-month', category: 'Coworking', name: 'Cowork Mérida - Monthly Pass', city: 'Mérida', description: 'Centro Histórico coworking with high-speed fiber, AC, free coffee/tea, kitchen, and weekly community events.', discount: 'First month $99 (regular $149)', valid_until: '2026-12-31', url: 'https://www.coworkmerida.com' },

  // === Language schools ===
  { id: 'iie-cdmx', category: 'Language Schools', name: 'IIE - Spanish Immersion Mexico City', city: 'Mexico City', description: '20 hours/week of small-group Spanish (max 6 students). Instituto Internacional de Estudios is the longest-running school in Roma Norte.', discount: '10% off first course block', valid_until: '2026-06-30', url: 'https://www.iie-mexico.com' },
  { id: 'spanish-academy-merida', category: 'Language Schools', name: 'Habla Hispana - Mérida', city: 'Mérida', description: 'One-on-one or small-group Spanish in a colonial home in Centro. Optional homestay with a Yucateco family for total immersion.', discount: '15% off 4-week intensive', valid_until: '2026-09-30', url: 'https://www.hablahispana.com' },
  { id: 'spanish-academy-sma', category: 'Language Schools', name: 'Warren Hardy Spanish School', city: 'San Miguel de Allende', description: 'The "gold standard" for expats in SMA - structured 8-level curriculum, mostly retirees and digital nomads.', discount: '10% off Level 1 + 2 bundle', valid_until: '2026-12-31', url: 'https://www.warrenhardy.com' },

  // === Restaurants ===
  { id: 'pujol-tasting', category: 'Restaurants', name: 'Pujol - Tasting Menu', city: 'Mexico City', description: 'Enrique Olvera\'s world-famous Polanco restaurant. The mole madre is now 2,000+ days old.', discount: '$50 wine pairing credit with full tasting menu', valid_until: '2026-12-31', url: 'https://pujol.com.mx' },
  { id: 'kaab-tulum', category: 'Restaurants', name: 'Kaab Tulum', city: 'Playa del Carmen', description: 'Open-fire Yucatecan cuisine in a jungle setting just south of Playa. Smoked octopus, cochinita pibil, mezcal flights.', discount: '15% off dinner Mon-Wed', valid_until: '2026-09-30', url: 'https://kaab.mx' },
  { id: 'la-chaya-maya', category: 'Restaurants', name: 'La Chaya Maya', city: 'Mérida', description: 'The Yucatecan classics done right - sopa de lima, poc chuc, papadzules. Two locations in Centro.', discount: 'Free chaya juice with any entrée', valid_until: '2026-12-31', url: 'https://www.lachayamaya.com' },

  // === Health services ===
  { id: 'medicall-cdmx', category: 'Health Services', name: 'MediCall - English-Speaking GP', city: 'Mexico City', description: 'Network of bilingual MDs across CDMX. House calls + clinic visits, accepts SafetyWing & Cigna Global.', discount: 'First consultation 50% off', valid_until: '2026-12-31', url: 'https://www.medicallhome.com' },
  { id: 'amerimed-pv', category: 'Health Services', name: 'AmeriMed Hospital', city: 'Puerto Vallarta', description: 'JCI-accredited hospital with English-speaking staff. 24/7 ER, full diagnostics, specialist network. Most major US insurance accepted.', discount: '20% off annual physical', valid_until: '2026-12-31', url: 'https://www.amerimed-hospitals.com' },

  // === Activities ===
  { id: 'lucha-libre-cdmx', category: 'Activities', name: 'Lucha Libre Night at Arena México', city: 'Mexico City', description: 'Friday-night lucha experience with bilingual guide, transport from Roma/Condesa, and a post-show taco crawl.', discount: '15% off group tour for Mundalo members', valid_until: '2026-12-31', url: 'https://luchaexperience.com' },
  { id: 'cenote-day-tulum', category: 'Activities', name: 'Cenote Day Tour - 3 Cenotes', city: 'Playa del Carmen', description: 'Small-group tour to 3 cenotes near Playa: one open, one cave, one semi-open. Lunch + transport included.', discount: 'Free GoPro rental', valid_until: '2026-12-31', url: 'https://www.cenotemexico.com' },
  { id: 'hot-air-balloon-sma', category: 'Activities', name: 'Hot Air Balloon - San Miguel Sunrise', city: 'San Miguel de Allende', description: '45-min sunrise flight over Centro and the surrounding chichimeca highlands. Champagne breakfast on landing.', discount: '$30 off per person, 2+ travelers', valid_until: '2026-12-31', url: 'https://www.sanmiguelballoon.com' },
  { id: 'cooking-class-pv', category: 'Activities', name: 'Mexican Cooking Class with Chef Felipe', city: 'Puerto Vallarta', description: '3-hour class at a private home in Olas Altas - market visit, 4-course meal, mezcal tasting. Max 8 people.', discount: '15% off + free recipe book', valid_until: '2026-09-30', url: 'https://chefefelipe.com' },
];

const FilterDropdown = ({ value, onChange, options, allLabel, testid }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="bg-white border border-[#EFE6CF] rounded-full px-4 py-2 text-sm text-[#0A1F2E] focus:outline-none focus:ring-2 focus:ring-[#2CA8A0]/40"
    data-testid={testid}
  >
    <option value="">{allLabel}</option>
    {options.map((o) => (
      <option key={o} value={o}>{o}</option>
    ))}
  </select>
);

const DealCard = ({ d }) => {
  const expired = new Date(d.valid_until) < new Date();
  return (
    <article
      className={`bg-white border rounded-xl overflow-hidden flex flex-col transition ${
        expired ? 'opacity-50 border-[#0A1F2E]/8' : 'border-[#EFE6CF] hover:border-[#2CA8A0]/40'
      }`}
      data-testid={`local-deal-${d.id}`}
    >
      {d.image_url ? (
        <div className="aspect-[16/9] bg-[#F4ECD8] overflow-hidden border-b border-[#EFE6CF]">
          <img
            src={d.image_url}
            alt={d.name}
            loading="lazy"
            className="w-full h-full object-cover"
            data-testid={`local-deal-image-${d.id}`}
          />
        </div>
      ) : null}
      <div className="px-5 py-3 bg-[#F4ECD8] border-b border-[#EFE6CF] flex items-center justify-between">
        <span className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#1A5C6E]">
          {d.category}
        </span>
        {expired && (
          <span className="font-mono uppercase text-[9px] tracking-[0.15em] text-red-700 bg-red-50 px-2 py-0.5 rounded">
            Expired
          </span>
        )}
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <h3 className="font-display italic text-xl text-[#0A1F2E] leading-tight mb-2">
          {d.name}
        </h3>
        <p className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/55 mb-3 flex items-center gap-1">
          <MapPin className="w-3 h-3" /> {d.city}
        </p>
        <p className="font-body text-sm text-[#0A1F2E]/75 mb-3 flex-1 leading-relaxed">
          {d.description}
        </p>
        <p className="font-body text-sm font-semibold text-[#1A5C6E] mb-3 border-l-2 border-[#E8A830] pl-3">
          {d.discount}
        </p>
        <p className="font-mono uppercase text-[9px] tracking-[0.15em] text-[#0A1F2E]/45 mb-4 flex items-center gap-1">
          <Calendar className="w-3 h-3" /> Valid through {new Date(d.valid_until).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/Chicago'})}
        </p>
        <a
          href={d.url}
          target="_blank"
          rel="noopener noreferrer"
          className={`inline-flex items-center justify-center gap-2 font-mono uppercase text-[11px] tracking-[0.16em] font-semibold py-3 px-5 rounded-full transition ${
            expired
              ? 'bg-[#0A1F2E]/10 text-[#0A1F2E]/40 pointer-events-none'
              : 'bg-[#E8A830] hover:bg-[#d59824] text-[#0A1F2E]'
          }`}
          data-testid={`local-deal-redeem-${d.id}`}
        >
          {expired ? 'Expired' : 'Redeem'} <ExternalLink className="w-3 h-3" />
        </a>
      </div>
    </article>
  );
};

const LockedState = () => (
  <div className="max-w-2xl mx-auto bg-[#F4ECD8] border border-[#EFE6CF] rounded-xl p-8 md:p-10 text-center" data-testid="local-deals-locked">
    <Lock className="w-8 h-8 text-[#1A5C6E] mx-auto mb-4" />
    <h2 className="font-display italic text-3xl text-[#0A1F2E] mb-3">Local Deals are a Community perk.</h2>
    <p className="font-body text-base text-[#0A1F2E]/75 mb-6">
      Unlock 15+ Mundalo-curated discounts on coworking spaces, language schools,
      restaurants, healthcare, and activities across Mexico. Updated weekly.
    </p>
    <a
      href="/portal/upgrade"
      className="inline-flex items-center gap-2 bg-[#E8A830] hover:bg-[#d59824] text-[#0A1F2E] font-mono uppercase text-[11px] tracking-[0.16em] font-semibold py-3 px-6 rounded-full transition"
      data-testid="local-deals-upgrade-cta"
    >
      Upgrade to Community - $9.99/mo
    </a>
  </div>
);

export default function PortalLocalDeals() {
  const { user } = useAuth();
  const role = user?.role || ROLES.FREE_TRIAL;
  const hasAccess = [ROLES.GUIDED, ROLES.COMMUNITY, ROLES.CONNECTED, ROLES.ADMIN].includes(role);

  const [deals, setDeals] = React.useState(FALLBACK_DEALS);
  const [cities, setCities] = React.useState([]);
  const [cityFilter, setCityFilter] = React.useState('');
  const [categoryFilter, setCategoryFilter] = React.useState('');
  const [submitOpen, setSubmitOpen] = React.useState(false);

  const reloadDeals = React.useCallback(() => {
    fetch(`${API}/api/local-deals`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { deals: null }))
      .then((d) => {
        if (Array.isArray(d.deals) && d.deals.length > 0) setDeals(d.deals);
      })
      .catch(() => { /* fallback already set */ });
  }, []);

  React.useEffect(() => {
    reloadDeals();
    fetch(`${API}/api/cities`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => {
        const items = Array.isArray(d) ? d : d.cities || [];
        setCities(items.map((c) => c.name).filter(Boolean).sort());
      })
      .catch(() => { /* leave cities empty - filter will fall back to deal-derived cities */ });
  }, [reloadDeals]);

  // City options: prefer the canonical /api/cities list. If it's empty (404 /
  // network), derive them from the actual deal data so the filter still works.
  const cityOptions = React.useMemo(() => {
    if (cities.length > 0) return cities;
    return Array.from(new Set(deals.map((d) => d.city).filter(Boolean))).sort();
  }, [cities, deals]);

  // AND filter - both selections must match if set.
  const filteredDeals = React.useMemo(() => {
    return deals.filter((d) => {
      if (cityFilter && d.city !== cityFilter) return false;
      if (categoryFilter && (d.category || 'Other') !== categoryFilter) return false;
      return true;
    });
  }, [deals, cityFilter, categoryFilter]);

  return (
    <PortalLayout activeTab="localDeals">
      <SEO
        title="Local Deals - Mundalo"
        description="Mundalo-curated discounts on coworking, language schools, restaurants, and activities across Mexico."
        path="/portal/local-deals"
      />

      <div className="max-w-6xl mx-auto px-4 md:px-8 py-8 md:py-12" data-testid="portal-local-deals-page">
        <header className="mb-8">
          <p className="font-mono uppercase text-[10px] tracking-[0.2em] text-[#1A5C6E] mb-2">
            The Life · Local Deals
          </p>
          <h1
            className="font-display italic text-4xl md:text-5xl text-[#0A1F2E] leading-[1.05] mb-3"
            data-testid="local-deals-headline"
          >
            Discounts for the life you're actually living.
          </h1>
          <p className="font-body text-base md:text-lg text-[#0A1F2E]/75 max-w-2xl">
            Curated Mexico-local deals from coworking spaces, Spanish schools,
            restaurants, doctors, and activity operators. <span className="font-semibold">Updated weekly.</span>{' '}
            Tap any card to redeem.
          </p>
        </header>

        {!hasAccess ? (
          <LockedState />
        ) : (
          <>
            {/* Submit-a-deal CTA for paid members */}
            <div className="flex justify-end mb-4">
              <button
                type="button"
                onClick={() => setSubmitOpen(true)}
                className="inline-flex items-center gap-2 bg-[#0A1F2E] hover:bg-[#1A5C6E] text-white font-mono uppercase text-[11px] tracking-[0.15em] py-2.5 px-4 rounded-full transition"
                data-testid="local-deals-open-submit"
              >
                <Plus className="w-3.5 h-3.5" />
                Submit a deal
              </button>
            </div>

            {/* Filters - city + category. AND logic, instant client-side. */}
            <div
              className="flex flex-wrap items-center gap-3 mb-8"
              data-testid="local-deals-filters"
            >
              <FilterDropdown
                value={cityFilter}
                onChange={setCityFilter}
                options={cityOptions}
                allLabel="All Cities"
                testid="local-deals-filter-city"
              />
              <FilterDropdown
                value={categoryFilter}
                onChange={setCategoryFilter}
                options={LOCAL_DEAL_CATEGORIES}
                allLabel="All Categories"
                testid="local-deals-filter-category"
              />
              {(cityFilter || categoryFilter) && (
                <button
                  type="button"
                  onClick={() => { setCityFilter(''); setCategoryFilter(''); }}
                  className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/55 hover:text-[#0A1F2E] px-3 py-2 transition"
                  data-testid="local-deals-filter-clear"
                >
                  Clear filters
                </button>
              )}
              <span
                className="ml-auto font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/55"
                data-testid="local-deals-result-count"
              >
                {filteredDeals.length} {filteredDeals.length === 1 ? 'deal' : 'deals'}
              </span>
            </div>

            {filteredDeals.length === 0 ? (
              <div
                className="bg-white border border-[#EFE6CF] rounded-2xl p-12 text-center"
                data-testid="local-deals-empty"
              >
                <Tag className="w-10 h-10 text-[#1A5C6E]/30 mx-auto mb-4" />
                <p className="font-display italic text-2xl text-[#0A1F2E] mb-2">No deals match.</p>
                <p className="text-sm text-[#0A1F2E]/65 max-w-sm mx-auto">
                  Try a different city or category - or clear the filters to see everything.
                </p>
              </div>
            ) : (
              <div
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
                data-testid="local-deals-grid"
              >
                {filteredDeals.map((d) => <DealCard key={d.id} d={d} />)}
              </div>
            )}
          </>
        )}
      </div>

      <SubmitLocalDealModal
        open={submitOpen}
        onClose={() => setSubmitOpen(false)}
        onSubmitted={reloadDeals}
      />
    </PortalLayout>
  );
}
